import React, { useState } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { alpha } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { logout, useAuth } from '../contexts/AuthContext.tsx';
import { useTenant } from './ContextTenant.tsx';
import { setTenantId as persistTenantId } from '../api/tenantHeader.ts';
import client from '../api/client.ts';
import { drawerWidth } from './Sidebar/Sidebar.tsx';
import { topBarHeights } from './Layout/layoutConstants.ts';

const TopBar: React.FC = () => {
  const [value, setValue] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId, setTenantId } = useTenant();
  const { user } = useAuth();
  const [tenantList, setTenantList] = useState<
    { id: number; displayName?: string; name?: string; logoUrl?: string; logo_url?: string }[]
  >([]);
  const normalizedRoles = (user?.roles || []).map((role) =>
    role.toUpperCase().replace(/^ROLE_/, ''),
  );

  const applyTenantFocus = (nextTenantId: number | null) => {
    if (tenantId !== nextTenantId) {
      localStorage.removeItem('saSelectedLocation');
      localStorage.removeItem('saSelectedSpace');

      const nextParams = new URLSearchParams(location.search);
      let scrubbedRouteParams = false;
      ['tenantId', 'locationId', 'spaceId'].forEach((key) => {
        if (nextParams.has(key)) {
          nextParams.delete(key);
          scrubbedRouteParams = true;
        }
      });

      if (scrubbedRouteParams) {
        const nextSearch = nextParams.toString();
        navigate(
          {
            pathname: location.pathname,
            search: nextSearch ? `?${nextSearch}` : '',
          },
          { replace: true },
        );
      }
    }
    setTenantId(nextTenantId);
    persistTenantId(nextTenantId);
  };

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get('/api/v1/super/tenants', {
          params: { page: 0, query: '' },
        });
        const list = Array.isArray(data) ? data : data.content || [];
        setTenantList(list);
      } catch {
        setTenantList([]);
      }
    })();
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && value.trim()) {
      navigate(`/search?q=${encodeURIComponent(value.trim())}`);
      setValue('');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const appBarStyles = user?.internalUser
    ? {
        left: `${drawerWidth}px`,
        width: `calc(100% - ${drawerWidth}px)`,
        zIndex: (theme: any) => theme.zIndex.drawer + 1,
      }
    : {};

  return (
    <AppBar position="fixed" color="transparent" elevation={0} sx={appBarStyles}>
      <Toolbar sx={{ display: 'flex', gap: 2, minHeight: topBarHeights }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="overline"
            sx={{ color: 'text.secondary', letterSpacing: '0.18em' }}
          >
            Internal 3100
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {normalizedRoles.includes('SUPER_ADMIN') ? 'Global operations' : 'Internal support'}
          </Typography>
        </Box>

        <Select
          size="small"
          value={tenantId || ''}
          onChange={(event) => {
            const raw = String(event.target.value || '');
            const next = raw ? Number(raw) : null;
            applyTenantFocus(next);
          }}
          sx={{
            bgcolor: alpha('#ffffff', 0.72),
            minWidth: 240,
            borderRadius: 999,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha('#172033', 0.12),
            },
          }}
        >
          <MenuItem value="">
            <Box display="flex" alignItems="center" gap={1.5}>
              <Avatar
                sx={{
                  width: 26,
                  height: 26,
                  bgcolor: alpha('#172033', 0.08),
                  color: '#172033',
                }}
              >
                G
              </Avatar>
              <span>All tenants</span>
            </Box>
          </MenuItem>
          {tenantList.map((tenant) => (
            <MenuItem key={tenant.id} value={tenant.id}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar
                  src={tenant.logoUrl || tenant.logo_url}
                  sx={{ width: 26, height: 26 }}
                >
                  {(tenant.displayName || tenant.name || 'T')
                    .charAt(0)
                    .toUpperCase()}
                </Avatar>
                <span>
                  {tenant.displayName || tenant.name || `Tenant ${tenant.id}`}
                </span>
              </Box>
            </MenuItem>
          ))}
        </Select>

        <TextField
          placeholder="Search users, tenants or routes"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={handleKeyDown}
          size="small"
          fullWidth
          variant="outlined"
          sx={{
            maxWidth: 460,
            bgcolor: alpha('#ffffff', 0.72),
            borderRadius: 999,
            '& .MuiOutlinedInput-root': {
              borderRadius: 999,
            },
          }}
        />

        {user?.internalUser ? (
          <>
            <Chip
              label={user.email || 'Internal access'}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Tooltip title="Logout">
              <IconButton color="inherit" onClick={handleLogout}>
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </>
        ) : null}
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
